import React, { createContext, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import { authAPI } from '../services/api'; // Adjust the path if needed

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log("isAuthenticated",isAuthenticated)

  // Load user from token
  const loadUser = async () => {
    if (token) {
      try {
        const res = await authAPI.getCurrentUser();
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error loading user:', err.response?.data || err.message);
        logout(); // logout on error
      }
    }
    setLoading(false);
  };

  // Register user
  const register = async (formData) => {
    try {
      const res = await authAPI.register(formData);
      return { success: true, data: res.data };
    } catch (err) {
      const msg = err.response?.data?.msg || 'Registration failed';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const res = await authAPI.login({ email, password });
      const token = res.data.token;
      setToken(token);
      localStorage.setItem('token', token);
      setIsAuthenticated(true);
      await loadUser();
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.msg || 'Invalid credentials';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout failed:', err.response?.data || err.message);
    }
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      const res = await authAPI.forgotPassword(email);
      return { success: true, data: res.data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Error processing request',
      };
    }
  };

  // Reset password
  // Verify OTP
  const verifyOtp = async (data) => {
    try {
      const res = await authAPI.verifyOtp(data);
      return { success: true, data: res };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Error verifying OTP',
      };
    }
  };

  // Resend OTP
  const resendOtp = async (data) => {
    try {
      const res = await authAPI.resendOtp(data);
      return { success: true, data: res };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Error resending OTP',
      };
    }
  };

  // Reset password
  const resetPassword = async (resetToken, password) => {
    try {
      const res = await authAPI.resetPassword(resetToken, password);
      return { success: true, data: res.data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Error resetting password',
      };
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  // Check token expiration
  const checkTokenExpiration = () => {
    if (token) {
      try {
        const decoded = jwt_decode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          logout();
          return false;
        }
        return true;
      } catch (err) {
        console.error('Invalid token:', err);
        logout();
        return false;
      }
    }
    return false;
  };

  // Clear error
  const clearError = () => setError(null);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  // Check token expiration every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (token) checkTokenExpiration();
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        loading,
        user,
        error,
        register,
        login,
        logout,
        forgotPassword,
        resetPassword,
        verifyOtp,
        resendOtp,
        isAdmin,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
